/**
 * Base → Create → Translate. Translate a collection document itself — a PDF in
 * is a PDF out, the layout kept — into another language with Azure AI
 * Translator. The translation is stored as a new document linked to its
 * original and indexed into the same collection, so it is searchable at once.
 *
 * Left: a new translation and the runs so far. Right: the selected run — live
 * status while it runs, then original and translation side by side. The
 * selected run is in the URL (`?run=`), so a result can be linked to.
 */

import { useCallback, useState } from "react";
import { Anchor, Grid, Paper, Stack, Text, Title } from "@mantine/core";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { RunList, RunStatus } from "@/features/solutions/shared";
import type { RunDetailOut, RunOut } from "@/shared/api/solutionRuns";
import { TranslateForm } from "./TranslateForm";
import { TranslateResultView } from "./TranslateResultView";
import { asTranslateResult } from "./translateMeta";

export function TranslatePage() {
  const [params, setParams] = useSearchParams();
  const runId = params.get("run");
  const [refreshKey, setRefreshKey] = useState(0);
  const [run, setRun] = useState<RunDetailOut | null>(null);

  const select = useCallback(
    (id: string) => {
      setRun(null);
      setParams({ run: id });
    },
    [setParams],
  );
  const onFinished = useCallback(() => setRefreshKey((k) => k + 1), []);
  const current = run && run.run_id === runId ? run : null;
  const result = current?.status === "succeeded" ? asTranslateResult(current.result) : null;

  return (
    <Stack gap="lg">
      <div>
        <Text size="sm">
          <Anchor component={RouterLink} to="/solutions/create">
            Create
          </Anchor>{" "}
          / Translate
        </Text>
        <Title order={2}>Translate</Title>
        <Text c="dimmed" size="sm">
          Translate a document from a collection into another language, with its layout kept. The
          translation is added to the same collection and indexed.
        </Text>
      </div>
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Stack gap="lg">
            <Paper withBorder radius="md" p="md">
              <Title order={4} mb="sm">
                New translation
              </Title>
              <TranslateForm
                onCreated={(created) => {
                  setRefreshKey((k) => k + 1);
                  select(created.run_id);
                }}
              />
            </Paper>
            <Paper withBorder radius="md" p="md">
              <Title order={4} mb="sm">
                Translations
              </Title>
              <RunList
                kind="translate"
                selectedRunId={runId}
                onSelect={(r: RunOut) => select(r.run_id)}
                refreshKey={refreshKey}
                emptyText="No translations yet."
              />
            </Paper>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 7 }}>
          {runId ? (
            <Stack gap="md">
              <RunStatus runId={runId} onUpdate={setRun} onFinished={onFinished} />
              {current && result && <TranslateResultView run={current} result={result} />}
            </Stack>
          ) : (
            <Paper withBorder radius="md" p="xl">
              <Text c="dimmed" size="sm">
                Start a translation, or pick one from the list to see the original and the
                translation side by side.
              </Text>
            </Paper>
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
